import '@fontsource/maven-pro/400.css';
import '@fontsource/maven-pro/500.css';
import '@fontsource/maven-pro/700.css';
import '@fontsource/rubik/400.css';
import '@fontsource/rubik/500.css';
import '@fontsource/rubik/700.css';
import '@src/assets/custom-grid-theme.css';
import '@src/index.css';

import { ApolloProvider } from '@apollo/client';
import { createTheme, grey, KeyboardArrowDownIcon, ThemeProvider } from '@ds/coolshop';
import { LanguageProvider } from '@modules/language';
import { useAuth } from '@src/hooks';
import { SnackbarProvider } from 'notistack';
import { StrictMode, useContext, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { IntlProvider, MessageFormatElement } from 'react-intl';

import { FindReplaceDialogHost } from './_new/modules/find-replace';
import { NotificationSubscriptionContextProvider } from './_new/modules/notifications';
import { GlobalIntlConfigurator } from './_new/utils/globalIntl';
import { IAuthContext } from './assets/types/auth';
import { DirtyContextProvider } from './components/hierarchies/shared/DirtyContext';
import { Routes } from './components/layout/Routes';
import { LinearLoader } from './components/shared/LinearLoader';
import { SnackbarUtilsConfigurator } from './components/shared/SnackBarUtils';
import { SettingProvider } from './modules/setting/SettingProvider';
import { SettingsContext } from './modules/setting/SettingsContext';
import BlockActionContextProvider from './store/BlockActionContext/BlockActionContext.provider';
import JobSubscriptionContextProvider from './store/JobSubscriptionContext/JobSubscriptionContext.provider';
import { getApolloClient } from './utils/generic/getApolloClient';
// fix type
type LangImportType = typeof import('@src/assets/lang/compiled-lang/fr.json');

const loadLocaleData = async (locale: string): Promise<LangImportType> => {
    // TODO REST call per lang.json
    switch (locale) {
        case 'fr':
            return (await import('@src/assets/lang/compiled-lang/fr.json')).default;
        case 'it':
            return (await import('@src/assets/lang/compiled-lang/it.json')).default;
        default:
            return (await import('@src/assets/lang/compiled-lang/en.json')).default;
    }
};

const locale = navigator.language.substring(0, 2);

declare module '@mui/material/styles' {
    interface Palette {
        disabled: string;
    }
    interface PaletteOptions {
        disabled: string;
    }

    interface ZIndex {
        topComponent: number;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        disabled: true;
    }
}
function ThemedApp({ sessionData }: { sessionData: IAuthContext['sessionData'] }) {
    const { theme: themeSettings } = useContext(SettingsContext);
    // const [darkMode, setDarkMode] = useState<boolean>(localStorage.getItem('darkMode') === 'true');
    const darkMode = false;
    // useEffect(() => {
    //     localStorage.setItem('darkMode', String(darkMode));
    // }, [darkMode]);

    // Scrolling the mouse wheel over a focused number input changes its value (default
    // browser behavior). Blur the focused number input on wheel so scrolling never
    // mutates the value.
    useEffect(() => {
        const handleWheel = () => {
            const active = document.activeElement;
            if (active instanceof HTMLInputElement && active.type === 'number') {
                active.blur();
            }
        };
        document.addEventListener('wheel', handleWheel);
        return () => document.removeEventListener('wheel', handleWheel);
    }, []);

    /**
     * Use this theme along all the app
     * - primary: main/light/dark blue
     * - secondary: main/light/dark orange
     */

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    action: {},
                    // The design system's own value, restated because this theme is built with
                    // MUI's createTheme and not on top of defaultTheme: an empty object here does
                    // not inherit it, it replaces it. It is the translucent pill behind a disabled
                    // field drawn on a coloured bar — the configurator's total price is one —
                    // which without it loses its background and stops being readable.
                    contrastAction: { disabledBackground: 'rgba(255, 255, 255, 0.12)' },
                    mode: darkMode ? 'dark' : 'light',
                    primary: {
                        main:
                            sessionData?.attributes?.primaryColor ??
                            themeSettings?.primaryColor ??
                            '#001560',
                    },
                    secondary: {
                        main:
                            sessionData?.attributes?.secondaryColor ??
                            themeSettings?.secondaryColor ??
                            '#057bff',
                    },
                    disabled: grey[200],
                },
                // DS @ds/coolshop input components read these variants from the theme to size
                // their value/label text; without them, Select/TextField values fall back to the
                // MUI default (~16px) instead of the DS-intended 12px.
                typography: {
                    // A tenant's own font stack (theme.fontFamily). The app ships Rubik and Maven
                    // Pro (the stylesheets imported above), which the browser downloads only when
                    // the stack uses them. Absent = the MUI default stack.
                    ...(themeSettings?.fontFamily && { fontFamily: themeSettings.fontFamily }),
                    subtitle3: {
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '18.84px',
                        letterSpacing: '0.1px',
                    },
                    'input-label': {
                        fontSize: '14px',
                        fontWeight: 400,
                        // The DS InputLabel clips its text with overflow:hidden, so a
                        // line-height equal to the font size leaves no room for descenders
                        // (g, q, p, y) and truncates them. Give the line box headroom.
                        lineHeight: 1.25,
                        letterSpacing: '0.15px',
                    },
                    'input-value': {
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '14px',
                        letterSpacing: '0.15px',
                    },
                },
                components: {
                    // Use the chevron icon for all Selects to match the @ds/coolshop
                    // Autocomplete; the DS Select otherwise inherits MUI's default filled arrow.
                    MuiSelect: {
                        defaultProps: {
                            IconComponent: KeyboardArrowDownIcon,
                        },
                    },
                    // Match Select dropdown option text to the input value size. The @ds/coolshop
                    // Select hardcodes 12px on the menu Paper via inline sx, so the override needs
                    // extra specificity (&&) to win over it.
                    MuiMenuItem: {
                        styleOverrides: {
                            root: ({ theme: t }) => ({
                                '&&.MuiButtonBase-root.MuiMenuItem-root': {
                                    fontSize: t.typography['input-value']?.fontSize,
                                },
                            }),
                        },
                    },
                    // Match Autocomplete dropdown option text to the input value size.
                    MuiAutocomplete: {
                        styleOverrides: {
                            option: ({ theme: t }) => ({
                                fontSize: t.typography['input-value']?.fontSize,
                            }),
                        },
                    },
                    // Restore the hover highlight on clickable ListItems used as menu rows
                    // (the @ds/coolshop ListItem has no hover of its own).
                    MuiMenu: {
                        styleOverrides: {
                            paper: ({ theme: t }) => ({
                                '& .MuiListItem-root:not(.Mui-disabled):hover': {
                                    backgroundColor: t.palette.action.hover,
                                    cursor: 'pointer',
                                },
                            }),
                        },
                    },
                    MuiListItemText: { styleOverrides: { primary: { fontSize: '0.9em' } } },
                    // The @ds/coolshop Tooltip hardcodes a 10px/14px bubble through an inline sx
                    // on its Popper, which is too small to read for the value + note texts the
                    // app puts in tooltips. That sx is a descendant rule, so the override needs
                    // extra specificity (&&&) to win over it.
                    MuiTooltip: {
                        styleOverrides: {
                            tooltip: {
                                '&&&': {
                                    fontSize: '13px',
                                    lineHeight: '18px',
                                    padding: '8px 12px',
                                    maxWidth: 420,
                                },
                            },
                        },
                    },
                    // Hide the native number-input spinner (up/down arrows) on all number
                    // inputs across browsers; the DS doesn't strip them by default. Applied at
                    // InputBase so it covers TextField, OutlinedInput, FilledInput and Input.
                    // A disabled field drawn on a coloured bar is the one place the MuiInputBase
                    // rule below gets wrong: there the value is not meant to read as disabled, it
                    // is a read-only display of a number — the configurator's total price — and
                    // text.disabled on a filled bar is invisible.
                    //
                    // `!important` rather than out-counting the repeated classes that rule uses:
                    // the margin would be a hand-counted one, and nothing would fail the build the
                    // day someone raises the other side for an unrelated reason. It also keeps the
                    // selector here down to what it means. Scoped to bars actually filled with the
                    // primary colour — `PimAppBar` is transparent and carries the global search,
                    // where contrastText would be white on white.
                    MuiAppBar: {
                        styleOverrides: {
                            root: ({ theme: t }) => ({
                                '&.MuiAppBar-colorPrimary .MuiInputBase-input.Mui-disabled': {
                                    color: `${t.palette.primary.contrastText} !important`,
                                    WebkitTextFillColor: `${t.palette.primary.contrastText} !important`,
                                },
                            }),
                        },
                    },
                    MuiInputBase: {
                        styleOverrides: {
                            root: ({ theme: t }) => ({
                                // A disabled value reads as disabled: the DS TextField keeps
                                // the full text colour with a six-class selector, so the
                                // disabled class is repeated to outrank it.
                                '&.MuiInputBase-root.MuiInputBase-root .MuiInputBase-input.Mui-disabled.Mui-disabled.Mui-disabled.Mui-disabled':
                                    {
                                        color: t.palette.text.disabled,
                                        WebkitTextFillColor: t.palette.text.disabled,
                                    },
                                // Values stand out from their labels: bold text in every input
                                // (text, select, autocomplete, date), placeholders excluded. The
                                // DS TextField sets the weight with a three-class selector, so
                                // this one repeats the root class to outrank it.
                                '&.MuiInputBase-root.MuiInputBase-root .MuiInputBase-input': {
                                    fontWeight: 700,
                                },
                                // A pseudo-element rule beats the inherited weight at any
                                // specificity, so this stays low enough for a component (e.g.
                                // the sidebar search) to keep a bold placeholder on purpose.
                                '& .MuiInputBase-input::placeholder': {
                                    fontWeight: 400,
                                },
                                '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                },
                                '& input[type=number]::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                                '& input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                            }),
                        },
                    },
                    MuiFormControl: {
                        defaultProps: {
                            size: 'small',
                        },
                    },
                    MuiButton: {
                        defaultProps: {
                            size: 'small',
                        },
                    },
                    // The @ds/data-table card paints only its cells, so every part of it
                    // without rows (the space under the last row, the empty/no-results state,
                    // the footer strip with the column manager and the pagination) is
                    // transparent and lets the themed page background show through inside the
                    // card. Fill the whole card like the rows: the selector matches the card
                    // root through the footer strip it always renders.
                    MuiStack: {
                        styleOverrides: {
                            root: ({ theme: t }) => ({
                                '&:has(> .MuiStack-root > .MuiTablePagination-root)': {
                                    backgroundColor: t.palette.background.paper,
                                },
                            }),
                        },
                    },
                },
                zIndex: {
                    topComponent: 2000,
                },
            }),
        [darkMode, sessionData, themeSettings?.fontFamily],
    );

    return (
        <ThemeProvider theme={theme}>
            <DirtyContextProvider>
                <BlockActionContextProvider>
                    <DndProvider backend={HTML5Backend}>
                        <SnackbarProvider maxSnack={3}>
                            <JobSubscriptionContextProvider>
                                <NotificationSubscriptionContextProvider>
                                    <SnackbarUtilsConfigurator />
                                    <GlobalIntlConfigurator />
                                    <FindReplaceDialogHost>
                                        <Routes />
                                    </FindReplaceDialogHost>
                                </NotificationSubscriptionContextProvider>
                            </JobSubscriptionContextProvider>
                        </SnackbarProvider>
                    </DndProvider>
                </BlockActionContextProvider>
            </DirtyContextProvider>
        </ThemeProvider>
    );
}
function App(): JSX.Element {
    const { initialize, isReady, error, sessionData } = useAuth();

    const [messages, setMessages] = useState<
        Record<string, string> | Record<string, MessageFormatElement[]>
    >({});

    useEffect(() => {
        initialize();
    }, []);

    useEffect(() => {
        if (!isReady) return;
        loadLocaleData(locale).then(setMessages);
    }, [isReady]);

    const apolloClient = getApolloClient();

    return !isReady ? (
        <LinearLoader message={error?.message} /> // TODO: remove this string
    ) : (
        <StrictMode>
            <ApolloProvider client={apolloClient}>
                <SettingProvider>
                    <LanguageProvider>
                        <IntlProvider
                            messages={messages}
                            locale={locale}
                            defaultLocale="en"
                            onError={err => {
                                if (err.code === 'MISSING_TRANSLATION') {
                                    // TODO: log the error
                                    return;
                                }
                                // eslint-disable-next-line no-console
                                console.error(err);
                            }}
                        >
                            <ThemedApp sessionData={sessionData} />
                        </IntlProvider>
                    </LanguageProvider>
                </SettingProvider>
            </ApolloProvider>
        </StrictMode>
    );
}

export default App;
